import React, {Fragment, useEffect, useState} from 'react';
import {
    Button,
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
    Input,
    Select,
} from "@headlessui/react";
import {
    ArrowPathIcon, CheckCircleIcon,
    DocumentArrowDownIcon,
    ExclamationTriangleIcon,
    IdentificationIcon, MinusCircleIcon, PlusCircleIcon,
    QueueListIcon, StopCircleIcon,
    UserGroupIcon,
} from "@heroicons/react/24/outline";
import {db} from "../firebase/firebase";
import {useAuth} from "../contexts/authContext";
import {useDarkMode} from "../contexts/darkModeContext";
import Loading from "./Widgets/Loading";
import CustomerSection from "./Customers/CustomerSection";
import * as XLSX from "xlsx";
import AccountView from "./Customers/AccountView";
import {doc, getDoc, updateDoc} from "firebase/firestore";
import CustomerSearchInput from "./Customers/CustomerSearchInput";
import {useNotification} from "./Notifications/NotificationProvider";
import {downloadExcelCustomer, numberWithCommas} from "./GLOBAL/GlobalFunctions";
import CustomButton from "./Widgets/CustomButton";



const Customers = () => {
    const { currentUser } = useAuth();
    
    const [alertsOn] = useState(() => {
        const storedSettings = localStorage.getItem('alertsOn');
        return storedSettings ? JSON.parse(storedSettings).alertsOn : false; // Default to false if null
    });

    const [status, setStatus] = useState("A");
    const [upload, setUpload] = useState(false);

    const { darkMode } = useDarkMode();
    const { triggerNotification } = useNotification();

    // const [selectedIndustry, setSelectedIndustry] = useState("");

    const [customers, setCustomers] = useState([]);
    const [inactive, setInactive] = useState([]);
    const [customersPending, setCustomersPending] = useState([]);
    const [sharedCustomers, setSharedCustomers] = useState([]);

    const [filteredPendingCustomers, setFilteredPendingCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [filteredInactive, setFilteredInactive] = useState([]);
    const [filteredSharedCustomers, setFilteredSharedCustomers] = useState([]);
    
    const [searchQuery, setSearchQuery] = useState("");

    const [updating, setUpdating] = useState(false);
    

    useEffect(() => {
        
        
        const fetchCustomersPending = async () => {
            if (!currentUser) {
                console.warn("No user is currently logged in. Cannot fetch pending customers.");
                return;
            }
        
            try {
                const storedPendingData = localStorage.getItem(`pendingCustomers_${currentUser.uid}`);
        
                if (storedPendingData) {
                    try {
                        const pendingData = JSON.parse(storedPendingData);
                        setCustomersPending(pendingData);
                        setFilteredPendingCustomers(pendingData);
                    } catch (jsonError) {
                        console.error("Error parsing stored pending customers data:", jsonError);
                        localStorage.removeItem(`pendingCustomers_${currentUser.uid}`); // Clear invalid data
                    }
                } else {
                    const pendingCustomersRef = db
                        .collection("Customers")
                        .doc(currentUser.uid)
                        .collection("pending");
        
                    const snapshot = await pendingCustomersRef.get();
        
                    if (!snapshot.empty) {
                        const pendingData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        setCustomersPending(pendingData);
                        setFilteredPendingCustomers(pendingData);
                        localStorage.setItem(`pendingCustomers_${currentUser.uid}`, JSON.stringify(pendingData));
                    } else {
                        console.info("No pending customers found for this user.");
                        setCustomersPending([]);
                        setFilteredPendingCustomers([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching customers data from Firestore:", error);
            }
        };
        
        const fetchCustomersActive = async () => {
            if (!currentUser) {
                console.warn("No user is currently logged in. Cannot fetch active customers.");
                return;
            }
        
            try {
                const storedActiveData = localStorage.getItem(`activeCustomers_${currentUser.uid}`);
        
                if (storedActiveData) {
                    try {
                        const customersData = JSON.parse(storedActiveData);
                        setCustomers(customersData);
                        setFilteredCustomers(customersData);
                    } catch (jsonError) {
                        console.error("Error parsing stored active customers data:", jsonError);
                        localStorage.removeItem(`activeCustomers_${currentUser.uid}`); // Clear invalid data
                    }
                } else {
                    const activeCustomersRef = db
                        .collection("Customers")
                        .doc(currentUser.uid)
                        .collection("clients");
        
                    const snapshot = await activeCustomersRef.get();
        
                    if (!snapshot.empty) {
                        const activeData = snapshot.docs.map(doc => ({ id: doc.id, salesID: currentUser.uid, ...doc.data() }));
                        setCustomers(activeData);
                        setFilteredCustomers(activeData);
                        localStorage.setItem(`activeCustomers_${currentUser.uid}`, JSON.stringify(activeData));
                    } else {
                        console.info("No active customers found for this user.");
                        setCustomers([]);
                        setFilteredCustomers([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching active customers data from Firestore:", error);
            }
        };
        
        const fetchCustomersInactive = async () => {
            if (!currentUser) {
                console.warn("No user is currently logged in. Cannot fetch active customers.");
                return;
            }
        
            try {
                const storedActiveData = localStorage.getItem(`inactiveCustomers_${currentUser.uid}`);
        
                if (storedActiveData) {
                    try {
                        const customersData = JSON.parse(storedActiveData);
                        setInactive(customersData);
                        setFilteredInactive(customersData);
                    } catch (jsonError) {
                        console.error("Error parsing stored active customers data:", jsonError);
                        localStorage.removeItem(`inactiveCustomers_${currentUser.uid}`); // Clear invalid data
                    }
                } else {
                    const inactiveCustomersRef = db
                        .collection("Customers")
                        .doc(currentUser.uid)
                        .collection("inactive");
        
                    const snapshot = await inactiveCustomersRef.get();
        
                    if (!snapshot.empty) {
                        const inactiveData = snapshot.docs.map(doc => ({ id: doc.id, salesID: currentUser.uid, ...doc.data() }));
                        setInactive(inactiveData);
                        setFilteredInactive(inactiveData);
                        localStorage.setItem(`inactiveCustomers_${currentUser.uid}`, JSON.stringify(inactiveData));
                    } else {
                        console.info("No inactive customers found for this user.");
                        setInactive([]);
                        setFilteredInactive([]);
                    }
                }
            } catch (error) {
                console.error("Error fetching inactive customers data from Firestore:", error);
            }
        };
        
        const fetchSharedCustomers = async () => {
            if (!currentUser) {
                console.warn("No user is currently logged in. Cannot fetch shared customer data.");
                return;
            }
        
            try {
                // Check if customer data is cached
                const storedCustomerData = localStorage.getItem(`SharedCustomers_${currentUser.uid}`);
        
                if (storedCustomerData) {
                    try {
                        const parsedCustomers = JSON.parse(storedCustomerData);
                        setSharedCustomers(parsedCustomers);
                        setFilteredSharedCustomers(parsedCustomers)
                        console.log("Successfully shared customer data:", parsedCustomers);
                        return; // Stop here if cached data exists
                    } catch (jsonError) {
                        console.error("Error parsing stored customer data:", jsonError);
                        localStorage.removeItem(`SharedCustomers_${currentUser.uid}`); // Clear invalid data
                    }
                }
        
                // Fetch sharedCustomers array from "Users/{currentUser.uid}"
                const userRef = doc(db, "Users", currentUser.uid);
                const userSnap = await getDoc(userRef);
        
                if (!userSnap.exists()) {
                    console.info("No shared customers found.");
                    setSharedCustomers([]);
                    setFilteredSharedCustomers([]);
                    return;
                }
        
                const userData = userSnap.data();
                const sharedCustomersArray = userData.sharedCustomers || [];
        
                if (sharedCustomersArray.length === 0) {
                    console.info("No shared customers assigned.");
                    setSharedCustomers([]);
                    setFilteredSharedCustomers([]);
                    return;
                }
        
                // Fetch actual customer data for each entry in sharedCustomersArray
                const customerPromises = sharedCustomersArray.map(async ({ sharedBy, customerID }) => {
                    const customerRef = doc(db, "Customers", sharedBy, "clients", customerID);
                    const customerSnap = await getDoc(customerRef);
        
                    if (customerSnap.exists()) {
                        return { id: customerSnap.id, ...customerSnap.data() };
                    } else {
                        console.warn(`Customer ${customerID} not found under ${sharedBy}`);
                        return null;
                    }
                });
        
                const fetchedCustomers = (await Promise.all(customerPromises)).filter(cust => cust !== null);
        
                // Store only the fetched customer data in state & cache
                setSharedCustomers(fetchedCustomers);
                setFilteredSharedCustomers(fetchedCustomers);
                localStorage.setItem(`SharedCustomers_${currentUser.uid}`, JSON.stringify(fetchedCustomers));
        
            } catch (error) {
                console.error("Error fetching shared customers from Firestore:", error);
            }
        };
        
        fetchCustomersPending();
        fetchCustomersActive();
        fetchCustomersInactive();
        fetchSharedCustomers();
        
    }, [currentUser]);


    const onUpdate = async () => {
        setUpdating(true);
        const startTime = Date.now();

        // Pending
        try {
            console.log("Updating Pending");
            
            const pendingCustomers = db.collection("Customers").doc(currentUser.uid).collection("pending");
            const snapshot = await pendingCustomers.get();

            if (!snapshot.empty) {
                const pendingData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCustomersPending(pendingData);
                setFilteredPendingCustomers(pendingData);
                localStorage.setItem(`pendingCustomers_${currentUser.uid}`, JSON.stringify(pendingData));
            } else {
                setCustomersPending([]);
                setFilteredPendingCustomers([]);
                localStorage.setItem(`pendingCustomers_${currentUser.uid}`, JSON.stringify([]));
                console.log('Customer document not found');
            }

        } catch (error) {
            console.error("Error updating customers", error);
        }

        // Active
        try {
            console.log("Updating Active");
            
            const customers = db.collection("Customers").doc(currentUser.uid).collection("clients");
            const snapshot = await customers.get();

            if (!snapshot.empty) {
                const activeData = snapshot.docs.map(doc => ({ id: doc.id, salesID: currentUser.uid, ...doc.data() }));
                setCustomers(activeData);
                setFilteredCustomers(activeData);
                localStorage.setItem(`activeCustomers_${currentUser.uid}`, JSON.stringify(activeData));
            } else {
                setCustomers([]);
                setFilteredCustomers([]);
                localStorage.setItem(`activeCustomers_${currentUser.uid}`, JSON.stringify([]));
                console.log('Customer document not found');
            }

        } catch (error) {
            console.error("Error updating customers", error);
        }
        
        // Inactive
        try {
            console.log("Updating inactive");
            
            const inactiveCustomersRef = db
                .collection("Customers")
                .doc(currentUser.uid)
                .collection("inactive");

            const snapshot = await inactiveCustomersRef.get();

            if (!snapshot.empty) {
                const inactiveData = snapshot.docs.map(doc => ({ id: doc.id, salesID: currentUser.uid, ...doc.data() }));
                setInactive(inactiveData);
                setFilteredInactive(inactiveData);
                localStorage.setItem(`inactiveCustomers_${currentUser.uid}`, JSON.stringify(inactiveData));
            } else {
                console.info("No inactive customers found for this user.");
                setInactive([]);
                setFilteredInactive([]);
            }
            
        } catch (error) {
            console.error("Error updating inactive customers", error);
        }
        
        // Shared
        try {
            console.log("Updating Shared");

            // Fetch sharedCustomers array from "Users/{currentUser.uid}"
            const userRef = doc(db, "Users", currentUser.uid);
            const userSnap = await getDoc(userRef);
        
            if (!userSnap.exists()) {
                console.info("User document not found.");
                setSharedCustomers([]);
                setFilteredSharedCustomers([]);
                return; // Exit function
            }
        
            const userData = userSnap.data();
        
            // If sharedCustomers is missing or not an array, exit early
            if (!userData.hasOwnProperty("sharedCustomers") || !Array.isArray(userData.sharedCustomers)) {
                console.info("sharedCustomers array does not exist.");
                setSharedCustomers([]);
                setFilteredSharedCustomers([]);
                setUpdating(false);
                return; // Exit function immediately
            }
        
            const sharedCustomersArray = userData.sharedCustomers;
        
            if (sharedCustomersArray.length === 0) {
                console.info("No shared customers assigned.");
                setSharedCustomers([]);
                setFilteredSharedCustomers([]);
                return;
            }
        
            // Fetch actual customer data for each entry in sharedCustomersArray
            const customerPromises = sharedCustomersArray.map(async ({ sharedBy, customerID }) => {
                if (!sharedBy || !customerID) return null;
        
                const customerRef = doc(db, "Customers", sharedBy, "clients", customerID);
                const customerSnap = await getDoc(customerRef);
        
                return customerSnap.exists() ? { id: customerSnap.id, ...customerSnap.data() } : null;
            });
        
            // Ensure Promise.all() only runs if there are valid promises
            const fetchedCustomers = (await Promise.all(customerPromises)).filter(Boolean);
        
            // Store only the fetched customer data in state & cache
            setSharedCustomers(fetchedCustomers);
            setFilteredSharedCustomers(fetchedCustomers);
            localStorage.setItem(`SharedCustomers_${currentUser.uid}`, JSON.stringify(fetchedCustomers));
        
        } catch (error) {
            console.error("Error updating shared customers:", error);
        }
        
        console.log("finished Updating");
        
        const endTime = Date.now();
        const timeElapsed = (endTime - startTime) / 1000;
        setUpdating(false);
        triggerNotification("custUpdated");
        
        if (alertsOn) {
            alert(`Customers Updated in ${timeElapsed} secs`);
        }

    };
    
    
    useEffect(() => {
        const checkCustomersUpdated = async () => {
            try {
                const userDocRef = doc(db, `Users/${currentUser.uid}`);
                const userSnap = await getDoc(userDocRef);
    
                if (userSnap.exists()) {
                    const userData = userSnap.data();
    
                    if (userData.customersUpdated === true) {
                        await onUpdate();
                        await updateDoc(userDocRef, {
                            customersUpdated: false
                        });
                        console.log("customersUpdated was true; onUpdate triggered and reset to false.");
                    }
                }
            } catch (error) {
                console.error("Error checking or updating customersUpdated:", error);
            }
        };
    
        // Run if newCustomer is true in localStorage
        const newCustomer = localStorage.getItem('newCustomer');
        if (newCustomer === 'true') {
            onUpdate();
            localStorage.setItem('newCustomer', 'false');
        }
    
        checkCustomersUpdated();
    
        // eslint-disable-next-line
    }, []);


    const handlePendingSearch = (query) => {
        setSearchQuery(query);
        const filtered = customersPending.filter((customer) =>
            customer.name.toLowerCase().includes(query.toLowerCase()) ||
            customer.customerCode?.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredPendingCustomers(filtered);
    }

    const handleActiveSearch = (query) => {
        setSearchQuery(query);
        const filtered = customers.filter((customer) =>
            customer.name.toLowerCase().includes(query.toLowerCase()) ||
            customer.customerCode?.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredCustomers(filtered);
    }
    
    const handleSharedSearch = (query) => {
        setSearchQuery(query);
        const filtered = sharedCustomers.filter((customer) =>
            customer.name.toLowerCase().includes(query.toLowerCase()) ||
            customer.customerCode?.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredSharedCustomers(filtered);
    }
    
    const handleInactiveSearch = (query) => {
        setSearchQuery(query);
        const filtered = inactive.filter((customer) =>
            customer.name.toLowerCase().includes(query.toLowerCase()) ||
            customer.customerCode?.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredInactive(filtered);
    }


    const toggleUpload = () => {
        setUpload(!upload);
    };


    const filterCustomersByIndustry = (industry) => {
        const filtered = customers.filter(customer => industry === '' || customer.industry === industry);
        setFilteredCustomers(filtered);
    };

    const filterPendingCustomersByIndustry = (industry) => {
        const filtered = customersPending.filter(customer => industry === '' || customer.industry === industry);
        setFilteredPendingCustomers(filtered);
    };
    
    const filterInactiveCustomersByIndustry = (industry) => {
        const filtered = inactive.filter(customer => industry === '' || customer.industry === industry);
        setFilteredInactive(filtered);
    };
    
    

    const filterCustomersByPricing = (pricing) => {
        const filtered = customers.filter(customer => pricing === '' || customer.priceType === pricing);
        setFilteredCustomers(filtered);
    };

    const filterPendingCustomersByPricing = (pricing) => {
        const filtered = customersPending.filter(customer => pricing === '' || customer.priceType === pricing);
        setFilteredPendingCustomers(filtered);
    };
    
    const filterInactiveCustomersByPricing = (pricing) => {
        const filtered = inactive.filter(customer => pricing === '' || customer.priceType === pricing);
        setFilteredInactive(filtered);
    };



    const filtering = (e) => {
        if(status === "A"){
            filterCustomersByIndustry(e.target.value);
        }
        if (status === "P"){
            filterPendingCustomersByIndustry(e.target.value);
        }
        if (status === "I"){
            filterInactiveCustomersByIndustry(e.target.value);
        }
    }

    const filteringPrice = (e) => {
        if(status === "A"){
            filterCustomersByPricing(e.target.value);
        }
        if (status === "P"){
            filterPendingCustomersByPricing(e.target.value);
        }
        if (status === "I"){
            filterInactiveCustomersByPricing(e.target.value);
        }
    }

    
//     const [count, setCount] = useState(0);
//
//     useEffect(() => {
//
//         if (status === "A"){
//             setCount(customers.length);
//         }
//         if (status === "P"){
//             setCount(customersPending.length);
//         }
//         if (status === "S"){
//             setCount(sharedCustomers.length);
//         }
//
// // eslint-disable-next-line
//     }, [status, customers, customersPending]);

    
    const statusMap = {
        A: {
            customers: filteredCustomers,
            isShared: false,
            emptyTitle: "You Have No Active Customers",
            emptyDescription: "You Must Wait For the Plant to Update Your Customer's Status",
        },
        P: {
            customers: filteredPendingCustomers,
            isShared: false,
            emptyTitle: "You Have No Pending Customers",
            emptyDescription: "You May Onboard New Customers in the New Customer Form",
        },
        S: {
            customers: filteredSharedCustomers,
            isShared: true,
            emptyTitle: "You Have No Shared Customers",
            emptyDescription: "A Primary Rep has to share a customer with you",
        },
        I: {
            customers: filteredInactive,
            isShared: false,
            emptyTitle: "You Have No Inactive Customers",
            emptyDescription: "You may Toggle a customer to be inactive from your active collection",
        },
    };

    const currentStatus = statusMap[status] || {};

    // const handleFileUpload = async (event) => {
    //   const file = event.target.files[0];
    //   if (!file) return;
    //
    //   try {
    //     // Read the Excel file
    //     const data = await file.arrayBuffer();
    //     const workbook = XLSX.read(data, { type: "array" });
    //
    //     // Assuming the first sheet is the one you want
    //     const sheetName = workbook.SheetNames[0];
    //     const worksheet = workbook.Sheets[sheetName];
    //
    //     // Convert the sheet to JSON
    //     const jsonData = XLSX.utils.sheet_to_json(worksheet);
    //
    //     // Upload each row to Firestore
    //     const collectionRef = db.collection("Customers").doc(currentUser.uid).collection("clients");
    //
    //     const batch = db.batch(); // Use a batch write for efficiency
    //
    //     jsonData.forEach((row) => {
    //       const docRef = collectionRef.doc(); // Create a new document
    //
    //       // Add the additional fields
    //       const extendedRow = {
    //         ...row,                 // Spread the original row data
    //         status: "active",       // Add the status field
    //         existing: true,         // Add the existing field
    //       };
    //
    //       batch.set(docRef, extendedRow); // Write the extended row data to Firestore
    //     });
    //
    //     await batch.commit(); // Execute the batch
    //       if (alertsOn) {
    //           alert("Data uploaded successfully!");
    //       }
    //   } catch (error) {
    //     console.error("Error uploading file:", error);
    //     if (alertsOn) {
    //         alert("Failed to upload data.");
    //     }
    //   }
    // };


const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });

    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    const jsonData = XLSX.utils.sheet_to_json(worksheet);

    const collectionRef = db
      .collection("Customers")
      .doc(currentUser.uid)
      .collection("clients");

    const batch = db.batch();

    jsonData.forEach((row) => {
      const docRef = collectionRef.doc();

      let county = "";
      let fips = "";

      // 🔥 HANDLE COUNTY COLUMN
      if (row.county) {
        const raw = row.county.trim();

        // Example: "Monmouth 34-025"
        const match = raw.match(/^(.+?)\s+(\d{2})-(\d{3})$/);

        if (match) {
          county = match[1]; // "Monmouth"
          fips = `${match[2]}${match[3]}`; // "34025"
        } else {
          county = raw; // fallback
        }
      }

      const extendedRow = {
        ...row,
        county,     // cleaned county
        fips,       // new field
        status: "active",
        existing: true,
      };

      batch.set(docRef, extendedRow);
    });

    await batch.commit();

    if (alertsOn) {
      alert("Data uploaded successfully!");
    }
  } catch (error) {
    console.error("Error uploading file:", error);
    if (alertsOn) {
      alert("Failed to upload data.");
    }
  }
};
    
    const [accountView, setAccountView] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    
    const toggleAccountView = () => {
        if (!selectedCustomer) {
            setShowPopup(true); // Show popup if no customer is selected
        } else {
            setAccountView(!accountView);
        }
    };
    
    // Function to handle both accountView and selected customer
    const handleViewAccount = (customer) => {
        setAccountView(true);
        setSelectedCustomer(customer);
    };
    
    
    const downloadMapping = () => {
        if (status === "A") {
            downloadExcelCustomer(customers, `ActiveCustomers.xlsx`);
        }
        if (status === "P") {
            downloadExcelCustomer(customersPending, "PendingCustomers.xlsx");
        }
        if (status === "S") {
            downloadExcelCustomer(sharedCustomers, "SharedCustomers.xlsx");
        }
        if (status === "I") {
            downloadExcelCustomer(inactive, "InactiveCustomers.xlsx");
        }
    };

    
    const [secondaryNavigation, setSecondaryNavigation] = useState([
      // { name: 'Dashboard', href: '#', current: false },
      { name: 'Active', href: 'A', current: true },
      { name: 'Pending', href: 'P', current: false },
      { name: 'Shared', href: 'S', current: false },
      { name: 'Inactive', href: 'I', current: false },
    ]);
    
    const stats = [
      { name: 'Active Customers', stat: "A", value: customers.length },
      { name: 'Pending Customers', stat: "P", value: customersPending.length },
      { name: 'Shared Customers', stat: "S", value: sharedCustomers.length },
      { name: 'Inactive Customers', stat: "I", value: inactive.length },
    ]
    
    function classNames(...classes) {
      return classes.filter(Boolean).join(' ')
    }
    

    return (
        <div className={`flex h-[calc(100dvh-3.5rem)] overflow-hidden w-full transition-all duration-500 ease-in-out`}>

            {updating && (
                <Loading/>
            )}
            
            
            {/*Redesign*/}
            <>
                
                <main className={`animate-fadeIn h-[calc(100dvh-3.5rem)] overflow-y-auto scrollbar-hide z-20 w-full ${darkMode ? 'border-neutral-700 bg-darkMainColor text-white' : 'bg-white text-black'}`}>
                
                    {/*Header*/}
                    <div className="relative isolate overflow-auto">
                        
                        {/* Secondary navigation */}
                        <header className={`py-2 border-b ${darkMode ? 'border-neutral-700' : ''}`}>
                            
                            <div className="mx-auto flex flex-wrap items-center gap-3 px-3 sm:flex-nowrap">
                                
                                <CustomButton
                                    icon={ArrowPathIcon}
                                    text={`Sync`}
                                    onClick={onUpdate}
                                    color={`green`}
                                />
                                
                                <div className={`flex flex-col`}>
                                    <h1 className="font-semibold">Customer Management</h1>
                                    {/*<h1 className="text-xxs -mt-0.5 text-gray-500">Customers from your Sale Rep's</h1>*/}
                                </div>
                                
                                
                                <div
                                    className={`order-last flex w-full gap-x-8 font-semibold sm:order-none sm:w-auto sm:border-l ${darkMode ? 'sm:border-neutral-700' : ''} sm:pl-6 sm:text-sm/7`}>
                                    {secondaryNavigation.map((item) => (
                                        <div
                                            onClick={() => {
                                                setStatus(item.href);
                                                setSecondaryNavigation((nav) =>
                                                    nav.map((navItem) => ({
                                                        ...navItem,
                                                        current: navItem.href === item.href,
                                                    }))
                                                );
                                            }}
                                            key={item.name}
                                            className={`${item.current ? 'text-green-600 underline text-sm tracking-wide' : 'text-gray-500 text-xs'} duration-500  cursor-pointer`}
                                        >
                                            {item.name}
                                        </div>
                                    ))}
                                </div>
                                
                                {/*Buttons*/}
                                <div className={`ml-auto gap-x-3 flex items-center`}>
                                    
                                    {/*<ActionPillButton*/}
                                    {/*    label={`Export Customers`}*/}
                                    {/*    onClick={exportCustomersToExcel}*/}
                                    {/*    color={`green`}*/}
                                    {/*    icon={DocumentArrowDownIcon}*/}
                                    {/*/>*/}
                                    
                                    <CustomButton
                                        icon={accountView ? QueueListIcon : IdentificationIcon}
                                        text={accountView ? 'Return To List' : 'Account View'}
                                        onClick={toggleAccountView}
                                        color={`blue`}
                                        disabled={!selectedCustomer}
                                    />
                                    
                                </div>
                            
                            </div>
                        
                        </header>
                        
                        {/* Stats */}
                        <div
                            className={`border-b ${accountView ? 'hidden' : ''} ${darkMode ? 'bg-gray-700/10 border-b-neutral-700 lg:border-t-neutral-700' : 'bg-neutral-300/50 border-b-gray-900/10 lg:border-t-gray-900/5'}`}>
                            <dl className="mx-auto w-full">
                                
                                {(() => {
                                    const stats = [
                                        {
                                            label: "Active",
                                            value: `${numberWithCommas(customers.length)}`,
                                            previous: "All Pending and Active customers",
                                            icon: CheckCircleIcon,
                                            color: 'text-green-600',
                                            bgColor: 'bg-green-600',
                                            stat: "A",
                                            positive: true,
                                        },
                                        {
                                            label: "Pending",
                                            value: `${numberWithCommas(customersPending.length)}`,
                                            previous: "All Customers that can place orders",
                                            icon: MinusCircleIcon,
                                            color: 'text-yellow-600',
                                            bgColor: 'bg-yellow-600',
                                            stat: "P",
                                            positive: true,
                                        },
                                        {
                                            label: "Shared",
                                            value: `${numberWithCommas(sharedCustomers.length)}`,
                                            previous: "All Customers waiting to be onboarded",
                                            icon: MinusCircleIcon,
                                            color: 'text-blue-600',
                                            bgColor: 'bg-blue-600',
                                            stat: "S",
                                            positive: false,
                                        },
                                        {
                                            label: "Inactive",
                                            value: numberWithCommas(inactive.length),
                                            previous: "Customers that are not active",
                                            icon: StopCircleIcon,
                                            color: 'text-red-600',
                                            bgColor: 'bg-red-600',
                                            stat: "I",
                                            positive: false,
                                        },
                                    ];
                                    
                                    return (
                                        <div
                                            className={`grid grid-cols-4 overflow-hidden divide-x ${darkMode ? 'divide-neutral-700' : ''}`}>
                                            {stats.map((stat, index) => {
                                                
                                                const isActive = stat.stat === status;
                                                
                                                return (
                                                    <a
                                                        key={index}
                                                        href="#"
                                                        className={`block p-2 relative ${isActive ? `${stat.bgColor} bg-opacity-10` : ''}`}
                                                    >
                                                        <div className="flex flex-col lg:flex-row gap-y-1 gap-x-3">
                                                            <stat.icon
                                                                className={`shrink-0 size-5 text-muted-foreground stroke-2 ${stat.color}`}/>
                                                            
                                                            <div className="grow">
                                                                
                                                                {/*Title*/}
                                                                <p className="text-xs uppercase font-medium text-gray-500">
                                                                    {stat.label}
                                                                </p>
                                                                
                                                                {/*Value*/}
                                                                <h3 className="text-xl -mt-1 sm:text-2xl font-semibold">
                                                                    {stat.value}
                                                                </h3>
                                                                
                                                                {/*Previous*/}
                                                                <div
                                                                    className="flex -mt-1 justify-between items-center text-gray-500">
                                                                    
                                                                    <p className="text-xs">
                                                      <span className="italic">
                                                        {stat.previous}
                                                      </span>
                                                                    </p>
                                                                
                                                                </div>
                                                            
                                                            </div>
                                                        </div>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            
                            </dl>
                        </div>
                    
                    
                    </div>
                    
                    
                    {/*Table*/}
                    <div className={`${accountView ? 'hidden' : ''}`}>
                        
                        
                        {/* Recent activity table */}
                        <div>
                            
                            {/*Search Bar*/}
                            <div className={`w-full items-center py-1 gap-x-3 pr-3 h-fit flex ${darkMode ? 'text-white' : 'text-black'}`}>
                                
                                {/*Search Bar*/}
                                <div className="w-full h-fit text-sm">
                                    
                                    
                                    <div className={`${darkMode ? 'text-white' : 'text-black'} w-full flex items-center justify-center hover:scale-y-105`}>
                                        <CustomerSearchInput
                                            status={status}
                                            searchQuery={searchQuery}
                                            setSearchQuery={setSearchQuery}
                                            onSearch={
                                                status === "A" ? handleActiveSearch :
                                                status === "P" ? handlePendingSearch :
                                                status === "S" ? handleSharedSearch :
                                                status === "I" ? handleInactiveSearch :
                                                () => {
                                                }
                                            }
                                            darkMode={darkMode}
                                        />
                                    </div>
                                
                                
                                </div>
                                
                                {/*Industry*/}
                                <div className="w-fit text-sm">
                                    
                                    <div className="flex flex-col w-fit mr-3 h-full">
                                        {/*<div className="text-sm mb-1">Industry</div>*/}
                                        
                                        <Select
                                            className={`bg-transparent ${darkMode ? 'text-white' : 'text-black'} focus:outline-none h-full drop-shadow shadow-gray-900 py-1 px-4 rounded-md hover:scale-y-105`}
                                            // onChange={(e) => filterCustomersByIndustry(e.target.value)}
                                            onChange={filtering}
                                        >
                                            <option value="">Select Industry...</option>
                                            <option value="BOTANICAL">Botanical Garden</option>
                                            <option value="DISTRIB">Product Distributor</option>
                                            <option value="EMPLOYEE">PFC-EMPLOYEE</option>
                                            <option value="FARM">Agriculture - Farm</option>
                                            <option value="GOLF">Golf Course</option>
                                            <option value="GOVERNMENT">Government Entity</option>
                                            <option value="INDUSTRIAL">Industrial</option>
                                            <option value="LAWNTREE">Lawn Care & Tree Care</option>
                                            <option value="MRSALE">One Time Purchase Expected</option>
                                            <option value="MUNICIPAL">Municipal/ School</option>
                                            <option value="Non-Profit">Non-Profit Account</option>
                                            <option value="OTHER">Other</option>
                                            <option value="SPORTFLD">Sportsfield</option>
                                        </Select>
                                    </div>
                                
                                </div>
                                
                                {/*Pricing Type*/}
                                <div className="w-fit text-sm">
                                    
                                    <div className="flex flex-col w-fit mr-3 h-full">
                                        {/*<div className="text-sm mb-1">Pricing Type</div>*/}
                                        <Select
                                            className={`bg-transparent ${darkMode ? 'text-white' : 'text-black'} focus:outline-none h-full drop-shadow shadow-gray-900 py-1 px-4 rounded-md hover:scale-y-105`}
                                            onChange={filteringPrice}
                                        >
                                            <option value="">Select Pricing Type...</option>
                                            <option value="AG">Agriculture</option>
                                            <option value="DIST1">Distributor NE/INTL</option>
                                            <option value="DIST2">Distributor SE/CAN</option>
                                            <option value="NE">Northeast</option>
                                            <option value="SE">Southeast</option>
                                        </Select>
                                    </div>
                                
                                </div>
                            
                            
                            </div>
                            
                            
                            {/*Table*/}
                            <div className={`overflow-hidden border-t ${darkMode ? 'border-neutral-700' : 'border-gray-100'}`}>
                                <div className="mx-auto">
                                    <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                                        
                                        
                                        {/*CUSTOMERS MAP*/}
                                        <div
                                            className={`overflow-y-auto scrollbar-hide ${darkMode ? 'text-white bg-darkMainColor' : 'text-black bg-white'} rounded-lg h-[calc(100dvh-15rem)] scroll`}>
                                            
                                            <CustomerSection
                                                customers={currentStatus.customers || []}
                                                emptyTitle={currentStatus.emptyTitle}
                                                emptyDescription={currentStatus.emptyDescription}
                                                darkMode={darkMode}
                                                onUpdate={onUpdate}
                                                // setAccountView={setAccountView}
                                                handleViewAccount={handleViewAccount}
                                                isShared={currentStatus.isShared}
                                                plant={false}
                                            />
                                        
                                        </div>
                                    
                                    
                                    </div>
                                </div>
                            </div>
                            
                        </div>
                    
                    
                    </div>
                    
                    
                    {accountView && (
                        
                        <div className={`px-2 pb-2`}>
                            <AccountView
                                customer={selectedCustomer}
                                onUpdate={onUpdate}
                                plant={false}
                            />
                        </div>
                    
                    )}
                
                
                </main>
            
            </>
            
            
            {upload && (

                <div
                    id="crud-modal"
                    tabIndex="-1"
                    aria-hidden={!upload}
                    className={`fixed top-0 right-0 left-0 z-50 w-full h-[calc(100%-1rem)] max-h-full overflow-y-auto overflow-x-hidden justify-center items-center ${
                        upload ? 'flex' : 'hidden'
                    }`}
                >
                    <div className="relative z-50 p-4 w-1/3 max-h-full">

                        {/* Modal content */}
                        <div className={`relative ${darkMode ? 'bg-neutral-800 border border-darkBgColor text-white' : 'bg-white drop-shadow-lg text-black'} rounded-lg animate-zoomIn`}>


                            {/* Modal header */}
                            <div className="flex items-center justify-between p-5 border-b rounded-t dark:border-gray-600">

                                <div className={`w-full flex flex-col`}>
                                    <h3 className="text-lg w-full font-semibold">
                                         Upload Existing Customer Xcell Spreadsheet
                                    </h3>
                                </div>

                                <button
                                    type="button"
                                    className="text-gray-400 bg-transparent hover:animate-pulse hover:bg-gray-200 hover:text-gray-900 rounded-full text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                                    onClick={toggleUpload}
                                >
                                    <svg
                                        className="w-3 h-3"
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 14 14"
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7l-6 6"
                                        />
                                    </svg>
                                </button>
                            </div>


                            {/* Modal body */}
                            <form className="p-5"
                                  // onSubmit={onSubmitMain}
                                  method="POST">


                                {/*Row 1*/}
                                <div className="grid gap-3 mb-3 grid-cols-2">

                                    {/*Customer Code*/}
                                    <div className="col-span-2 sm:col-span-1">
                                        <label
                                            htmlFor="name"
                                            className="block mb-2 text-sm font-medium"
                                        >
                                            Customer Code
                                        </label>
                                        <Input type="file" accept=".xlsx, .xls" onChange={handleFileUpload}/>
                                    </div>
                                
                                </div>
                                
                            </form>


                        </div>
                    </div>
                </div>

            )}
            
            <Dialog open={showPopup} onClose={setShowPopup} className="relative z-50">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                />
        
              <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <DialogPanel
                    transition
                    className={`relative transform overflow-hidden rounded-lg ${darkMode ? 'bg-darkMainColor text-white' : 'bg-white text-black'} px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
                  >
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-600/15 sm:mx-0 sm:size-10">
                        <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <DialogTitle as="h3" className="text-base font-semibold">
                          No Customer Selected
                        </DialogTitle>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                                Click the expand button on any customer account in the list below to open the Account View. Once selected, the Account View will automatically display.                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <Button
                        type="button"
                        onClick={() => setShowPopup(false)}
                        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      >
                        I understand
                      </Button>
                      <Button
                        type="button"
                        data-autofocus
                        onClick={() => setShowPopup(false)}
                        className={`mt-3 inline-flex w-full justify-center rounded-md ${darkMode ? 'bg-darkBgColor' : 'bg-white ring-1 ring-inset ring-gray-300'} px-3 py-2 text-sm font-semibold shadow-sm hover:bg-opacity-50 sm:mt-0 sm:w-auto`}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DialogPanel>
                </div>
              </div>
            </Dialog>


        </div>
    )
};


export default Customers;
